import { Head, useForm, Link } from '@inertiajs/react'
import { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface Props {
  flash: {
    alert?: string
    notice?: string
  }
}

export default function New({ flash }: Props) {
  const { t } = useTranslation()
  const { data, setData, post, processing } = useForm({
    email_address: '',
    password: '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/session')
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Head title={t('auth.signIn.title')} />

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t('auth.signIn.title')}</CardTitle>
          <CardDescription>
            {t('auth.signIn.description')}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {flash.alert && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {flash.alert}
            </div>
          )}

          {flash.notice && (
            <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-600 dark:bg-green-900/20 dark:text-green-400">
              {flash.notice}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email_address">{t('common.email')}</Label>
              <Input
                id="email_address"
                type="email"
                placeholder={t('common.emailPlaceholder')}
                value={data.email_address}
                onChange={(e) => setData('email_address', e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('common.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('common.passwordPlaceholder')}
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                required
                autoComplete="current-password"
                maxLength={72}
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button type="submit" disabled={processing}>
                {processing ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
              </Button>

              <Link
                href="/passwords/new"
                className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              >
                {t('auth.signIn.forgotPassword')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
